import { Avatar, Icon } from "@uzverse/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@uzverse/core";
import { ApprovalCard } from "../components/ApprovalCard.js";
import { ErrorNote, TaskChip, useErrorText } from "../components/common.js";
import { MessageModelPill } from "../components/ModelPicker.js";
import type { MessagePreference } from "../components/ModelPicker.js";
import { TaskSteps } from "../components/TaskSteps.js";
import { useAssistant } from "../state/assistant.js";
import { shortModelName, useModelCatalog } from "../state/modelPolicy.js";
import { useSession } from "../state/session.js";
import { TERMINAL_STATES } from "../state/store.js";
import { useAppState } from "../state/useAppState.js";
import { usePrefFlag } from "../state/usePref.js";
import { useVoice } from "../voice/useVoice.js";
import { VoiceButton } from "../voice/VoiceButton.js";
import { useDockT } from "./dock-i18n.js";
import { useWorldT } from "./i18n.js";
import { pushWorldPref, syncWorldPrefs } from "./prefs.js";
import { navStore } from "./camera/nav.js";
import { applyTimeCommand, timeToast } from "./look/timeStore.js";
import { applyWeatherCommand, weatherToast } from "./look/weatherStore.js";
import { MaterialPicker } from "./MaterialPicker.js";
import { getWorldFocus, useWorldState, worldStore } from "./store.js";
import type { WorldStore } from "./store.js";
import { describeTarget } from "./target.js";
import "./world-dock.css";

const SPEAK_KEY = "uz.speakReplies";
/** The dock shows the latest turns only; the chat page keeps the whole history. */
const SHOWN_ITEMS = 4;

const readSpeak = (): boolean => {
  try {
    return globalThis.localStorage.getItem(SPEAK_KEY) === "1";
  } catch {
    return false;
  }
};

/** The dock starts collapsed (just the pill) and remembers what the user chose. */
export const DOCK_KEY = "uz.world.dock.open";

/** Which model and effort the run settled on (the `task.model` message), once it is known. */
function ResolvedModel({
  task,
}: {
  task: { model?: string | undefined; effort?: string | undefined };
}) {
  const t = useDockT();
  const { t: tw, i18n } = useTranslation("web");
  const catalog = useModelCatalog();
  if (!task.model) return null;
  const locale = i18n.language === "en" ? "en" : "tr";
  const model = shortModelName(
    catalog?.models.find((m) => m.id === task.model)?.label[locale] ?? task.model,
  );
  return (
    <span className="muted small dock-model" data-testid="dock-model">
      {task.effort
        ? t("modelUsedEffort", {
            model,
            effort: tw(`modelPick.effortLevel.${task.effort}.name`),
          })
        : t("modelUsed", { model })}
    </span>
  );
}

/** "Looking at: Demo Café · Floor 2 · north wall" with a clear button (nothing when empty). */
function LookingAtChip({ store }: { store: WorldStore }) {
  const t = useWorldT();
  const { selected } = useWorldState(store);
  const node = selected ? store.node(selected.id) : undefined;
  if (!selected || !node) return null;
  return (
    <div className="dock-chip" data-testid="dock-looking-at">
      <span className="dock-chip-text">
        {t("target.title")}: {describeTarget(t, node, selected.part)}
      </span>
      <button
        type="button"
        className="dock-chip-clear"
        aria-label={t("target.clear")}
        title={t("target.clear")}
        onClick={() => store.select(null)}
      >
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}

/**
 * The assistant on top of the 3D view: the same conversation, realtime client, steps card and
 * voice as the chat page, always sending the current world focus, so "make this wall red" works
 * without leaving the world. Collapsible; `/` jumps to the input.
 */
export function WorldDock({ store = worldStore }: { store?: WorldStore }) {
  const { t: tw, i18n } = useTranslation("web");
  const t = useDockT();
  const errorText = useErrorText();
  const { client } = useSession();
  const { name: assistantName } = useAssistant();
  const { items, tasks, approvals, connection, error } = useAppState();
  const [open, setOpenLocal] = usePrefFlag(DOCK_KEY, false);
  const [text, setText] = useState("");
  const [preference, setPreference] = useState<MessagePreference>({});
  const [speakReplies] = useState(readSpeak);
  const input = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const focusAfterOpen = useRef(false);
  const spoken = useRef(new Set<string>(items.map((i) => i.id)));
  const ui: "en" | "tr" = i18n.language === "en" ? "en" : "tr";
  // The open state is a user setting too: it follows the user across devices (localStorage is the cache).
  const setOpen = useCallback(
    (v: boolean) => {
      setOpenLocal(v);
      pushWorldPref({ dockOpen: v });
    },
    [setOpenLocal],
  );
  useEffect(() => {
    let live = true;
    let local: boolean | undefined;
    try {
      const stored = globalThis.localStorage.getItem(DOCK_KEY);
      local = stored === "1" ? true : stored === "0" ? false : undefined;
    } catch {
      local = undefined;
    }
    void syncWorldPrefs(local === undefined ? {} : { dockOpen: local }).then((p) => {
      if (live && p.dockOpen !== undefined) setOpenLocal(p.dockOpen);
    });
    return () => {
      live = false;
    };
  }, [setOpenLocal]);
  const voice = useVoice({ token: null, language: ui, uiLanguage: ui });

  // Finished assistant replies are read aloud when the user turned that on in the chat.
  useEffect(() => {
    for (const item of items) {
      if (item.role !== "assistant" || item.streaming || spoken.current.has(item.id)) continue;
      spoken.current.add(item.id);
      if (speakReplies) void voice.speak(item.text).catch(() => undefined);
    }
  }, [items, speakReplies, voice]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [items, approvals, open]);

  // "/" opens the dock and jumps to its input, from anywhere that is not a text field.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
      if (e.key !== "/" || typing || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      if (open) input.current?.focus();
      else {
        focusAfterOpen.current = true;
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  // Esc folds the dock away again.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) setOpen(false);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, setOpen]);
  useEffect(() => {
    if (open && focusAfterOpen.current) {
      focusAfterOpen.current = false;
      input.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, [text, open]);

  function send(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    voice.stopSpeaking();
    // "Set the world to night" is a client-side command: it works without the assistant.
    const time = applyTimeCommand(trimmed);
    if (time !== null) {
      navStore.toast({ kind: "info", ...timeToast(time) });
      setText("");
      return;
    }
    // "Yağmur yağdır" / "make it rain": the weather, also client side.
    const weather = applyWeatherCommand(trimmed);
    if (weather !== null) {
      navStore.toast({ kind: "info", ...weatherToast(weather) });
      setText("");
      return;
    }
    if (!client) return;
    // The focus is read now: what the user has selected goes with the command.
    // The pill's choice is for this one message; the next one starts on automatic again.
    client.sendChat(trimmed, getWorldFocus(store), i18n.language as Locale, preference);
    setPreference({});
    setText("");
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(text);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-primary dock-launcher"
        data-testid="dock-launcher"
        onClick={() => {
          focusAfterOpen.current = true;
          setOpen(true);
        }}
      >
        <Icon name="sparkles" size={16} />
        <span>{t("open")}</span>
        <kbd className="dock-kbd" aria-hidden="true">
          /
        </kbd>
        <span className="sr-only">{t("openHint")}</span>
      </button>
    );
  }

  const shown = items.slice(-SHOWN_ITEMS);
  const lastIndexOfTask = new Map<string, number>();
  shown.forEach((item, i) => {
    if (item.taskId) lastIndexOfTask.set(item.taskId, i);
  });

  return (
    <section className="world-dock" aria-label={t("title")} data-testid="world-dock">
      <header className="dock-head">
        <Avatar name={assistantName || "…"} assistant size={20} />
        <strong>{t("title")}</strong>
        <span
          role="status"
          className={`dot ${connection === "online" ? "dot-ok" : connection === "offline" ? "dot-warn" : "dot-busy"}`}
          title={tw(`chat.connection.${connection}`)}
        >
          <span className="sr-only">{tw(`chat.connection.${connection}`)}</span>
        </span>
        <button
          type="button"
          className="btn btn-quiet btn-icon dock-collapse"
          aria-label={t("collapse")}
          title={t("collapse")}
          onClick={() => setOpen(false)}
        >
          <Icon name="chevronDown" size={16} />
        </button>
      </header>

      <div className="dock-log" role="log" aria-label={t("log")} aria-live="polite">
        {shown.length === 0 ? (
          <p className="muted small">{t("hint")}</p>
        ) : (
          shown.map((item, i) => {
            const task = item.taskId ? tasks[item.taskId] : undefined;
            const showTask = item.taskId !== null && lastIndexOfTask.get(item.taskId) === i && task;
            return (
              <div key={`${item.role}-${item.id}`} className={`dock-msg dock-${item.role}`}>
                <p className="text">
                  {item.text}
                  {item.streaming && <span className="caret" aria-hidden="true" />}
                </p>
                {showTask && (
                  <div className="dock-meta">
                    <TaskChip state={task.state} />
                    <ResolvedModel task={task} />
                    <TaskSteps task={task} running={!TERMINAL_STATES.includes(task.state)} />
                  </div>
                )}
                {showTask &&
                  Object.values(approvals)
                    .filter((a) => a.taskId === item.taskId)
                    .map((a) => (
                      <ApprovalCard
                        key={a.id}
                        approval={a}
                        onDecide={(id, decision) => client?.respondToApproval(id, decision)}
                      />
                    ))}
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <ErrorNote message={error ? errorText(error) : null} />
      <LookingAtChip store={store} />
      <MaterialPicker store={store} />

      <form className="dock-form" onSubmit={submit}>
        <label className="sr-only" htmlFor="dock-input">
          {tw("chat.messageLabel")}
        </label>
        <textarea
          id="dock-input"
          ref={input}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("placeholder")}
          autoComplete="off"
          enterKeyHint="send"
        />
        <MessageModelPill value={preference} onChange={setPreference} />
        <VoiceButton voice={voice} onTranscript={send} disabled={!client} />
        <button
          type="submit"
          className="btn btn-primary btn-icon send-btn"
          disabled={!text.trim()}
          aria-label={t("send")}
          title={t("send")}
        >
          <Icon name="send" />
        </button>
      </form>
    </section>
  );
}
